import { useState } from "react";
import { Wallet, LogOut, LayoutGrid, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useWallet, detectWallets, WALLET_CATALOG } from "@/wallet/WalletContext";
import { useLang } from "@/i18n";
import { shortAddress } from "@/config/site";
import { toast } from "sonner";

export function ConnectWalletButton({ compact = false }) {
  const { t } = useLang();
  const { connected, address, connecting, connect, disconnect, solBalance } = useWallet();
  const [open, setOpen] = useState(false);
  const detected = detectWallets();

  const handleConnect = async (w) => {
    try {
      await connect(w.provider, w.name);
      setOpen(false);
      toast.success(`${w.name} connected`);
    } catch {
      toast.error(t("header.noWallet"));
    }
  };

  if (connected) {
    return (
      <div className="flex items-center gap-2" data-testid="wallet-connected">
        <Link to="/my-nfts" data-testid="wallet-address-chip"
          className="mono-chip border-neon/40 text-neon transition-colors hover:border-neon"
          title={address}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-neon" />
          {shortAddress(address)}
          {solBalance !== null && <span className="text-mist">· {solBalance.toFixed(2)} SOL</span>}
        </Link>
        <button
          type="button"
          onClick={disconnect}
          data-testid="disconnect-wallet-btn"
          aria-label={t("header.disconnect")}
          className="mono-chip border-white/10 transition-colors hover:border-red-400/50 hover:text-red-300"
        >
          <LogOut size={12} />
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="connect-wallet-btn"
        className={`btn-gold ${compact ? "!px-4 !py-2 !text-xs" : ""}`}
      >
        <Wallet size={15} />
        {connecting ? t("header.connecting") : t("header.connect")}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-panel text-paper sm:max-w-md" data-testid="wallet-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase tracking-widest text-goldlight">
              {t("header.connect")}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            {WALLET_CATALOG.map((c) => {
              const installed = detected.find((d) => d.key === c.key);
              return installed ? (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => handleConnect(installed)}
                  data-testid={`wallet-option-${c.key}`}
                  className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4 font-heading font-semibold transition-colors hover:border-neon/50 hover:bg-neon/5"
                >
                  {c.name}
                  <span className="mono-chip border-neon/40 text-neon">{shortAddress("") || "Detected"}</span>
                </button>
              ) : (
                <a
                  key={c.key}
                  href={c.installUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`wallet-install-${c.key}`}
                  className="flex w-full items-center justify-between rounded-xl border border-dashed border-white/15 px-5 py-4 font-heading font-semibold text-mist transition-colors hover:border-gold/50 hover:text-goldlight"
                >
                  {c.name}
                  <span className="mono-chip">
                    <ExternalLink size={11} /> Install
                  </span>
                </a>
              );
            })}
            <p className="flex items-start gap-2 pt-1 text-xs leading-relaxed text-mist">
              <LayoutGrid size={14} className="mt-0.5 shrink-0 text-gold" />
              {t("header.installHint")}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
